import {
  directionBarsForHorizon,
  directionHorizonFromBarsLabel,
  MARKET_REGIME_DIRECTION_DEADBAND_PCT,
} from "../../../../utils/marketRegimeDirectionLookbacks";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function normalizeToken(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function humanizeToken(value: string): string {
  return String(value ?? "").trim().replace(/_/g, " ");
}

function readFirstText(source: UnknownRecord, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function metricDefinitionText(definition: unknown): string | null {
  if (typeof definition === "string") return definition.trim() || null;
  if (!isRecord(definition)) return null;
  return readFirstText(definition, ["definition", "description", "meaning", "metric"]);
}

function variantDefinitionText(definition: unknown, variantKey: string): string | null {
  if (!isRecord(definition)) return null;
  const buckets = definition["buckets"];
  if (!isRecord(buckets)) return null;
  const target = normalizeToken(variantKey);
  for (const [bucketKey, bucketDef] of Object.entries(buckets)) {
    if (normalizeToken(bucketKey) !== target) continue;
    if (typeof bucketDef === "string" && bucketDef.trim()) return bucketDef.trim();
    if (!isRecord(bucketDef)) return null;
    return readFirstText(bucketDef, ["meaning", "rule", "definition", "description"]);
  }
  return null;
}

export function ensureSentence(text: string): string {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function lowercaseFirst(text: string): string {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

function resolveMetricDefinition(
  metricKey: string,
  defs: Record<string, unknown>,
): unknown {
  const k = String(metricKey ?? "").trim();
  if (!k) return undefined;
  if (defs[k] != null) return defs[k];
  const norm = normalizeToken(k);
  if (norm === "atr" && defs["atr_regime"] != null) return defs["atr_regime"];
  if (norm === "direction" && defs["trade_direction"] != null) return defs["trade_direction"];
  if (norm === "market_condition" && defs["regime"] != null) return defs["regime"];
  if (norm === "regime" && defs["market_condition"] != null) return defs["market_condition"];
  return defs[norm];
}

function firstSentence(text: string, maxLen = 110): string {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^[^.!?]+[.!?]?/);
  const sentence = (match?.[0] ?? trimmed).trim().replace(/\.$/, "");
  if (sentence.length <= maxLen) return sentence;
  return `${sentence.slice(0, maxLen - 3).trim()}...`;
}

const METRIC_SHORT_INTROS: Record<string, string> = {
  consistency: "How well market context matched your trade at entry",
  ma50: "Price compared to the 50-period moving average",
  ma200: "Price compared to the 200-period moving average",
  regime: "The market regime at entry",
  market_condition: "The market regime at entry",
  trade_direction: "Which side you traded",
  direction: "Which side you traded",
  adx: "Trend strength at entry",
  atr_regime: "Volatility level at entry",
  atr: "Volatility level at entry",
  bb_zone: "Where price sat inside the Bollinger bands",
  squeeze: "Whether volatility was compressed",
  htf_weighted: "Higher-timeframe alignment with your trade",
  sr_strength: "Strength of nearby support or resistance",
};

function shortMetricIntro(metricKey: string, metricText: string | null): string {
  const norm = normalizeToken(metricKey);
  if (METRIC_SHORT_INTROS[norm]) return METRIC_SHORT_INTROS[norm]!;
  if (metricText) return firstSentence(metricText);
  return `${humanizeToken(metricKey)} at entry`;
}

function shortVariantMeaning(variantText: string | null, fallback: string): string {
  if (!variantText) return fallback;
  return lowercaseFirst(firstSentence(variantText, 90) || fallback);
}

function candleDirectionBucketDefinition(
  metricKey: "candle_direction" | "candle_direction_short",
  variantKey: string,
  candleTimeframe?: string | null,
): string {
  const bars =
    metricKey === "candle_direction_short"
      ? directionBarsForHorizon("short", candleTimeframe)
      : directionBarsForHorizon("long", candleTimeframe);
  const variantNorm = normalizeToken(variantKey);
  const variantLabel = humanizeToken(variantKey);
  const variantMeaning =
    variantNorm === "up"
      ? "price rose over that window"
      : variantNorm === "down"
        ? "price fell over that window"
        : variantNorm === "flat"
          ? `price moved less than ${MARKET_REGIME_DIRECTION_DEADBAND_PCT}% (flat deadband)`
          : `price moved ${variantLabel}`;
  return ensureSentence(
    `Net price move over ~${bars} bars before entry. ${humanizeToken(variantKey)}: ${variantMeaning}.`,
  );
}

const REGIME_VARIANT_MEANINGS: Record<string, string> = {
  // Classifier v3 (regime_v3.1_standard)
  uptrend: "higher highs / higher lows with positive follow-through",
  downtrend: "lower highs / lower lows with negative follow-through",
  range: "sideways with no sustained directional progress",
  choppy: "sharp swings with poor follow-through / frequent reversals",
  unknown: "not enough history or data to classify",
  // Legacy v2 — display when loading older report rows
  expansion: "volatility and movement were elevated (legacy)",
  trend: "the market was trending with follow-through (legacy)",
  accumulation: "the market was compressing in a base-like range (legacy)",
  volatile: "volatility was elevated (legacy)",
};

/** Defines what a metric is and what a specific bucket/variant means. */
function composeBucketDefinitionTooltip(
  metricKey: string,
  variantKey: string,
  metricText: string | null,
  variantText: string | null,
  options?: MarketContextVariantTooltipOptions,
): string {
  const metricNorm = normalizeToken(metricKey);
  const variantNorm = normalizeToken(variantKey);
  const metricLabel = humanizeToken(metricKey);
  const variantLabel = humanizeToken(variantKey);
  const intro = shortMetricIntro(metricKey, metricText);

  const candleMetric = resolveCandleDirectionMetricKey(metricKey, options);
  if (candleMetric && isCandleDirectionVariant(variantKey)) {
    return candleDirectionBucketDefinition(candleMetric, variantKey, options?.candleTimeframe);
  }

  if (metricNorm === "trade_direction" || metricNorm === "direction") {
    const variantMeaning =
      variantNorm === "long"
        ? "buy-side (long) trades"
        : variantNorm === "short"
          ? "sell-side (short) trades"
          : shortVariantMeaning(variantText, `${variantLabel} trades`);
    return ensureSentence(`${intro}. ${variantLabel}: ${variantMeaning}.`);
  }

  if (metricNorm === "regime" || metricNorm === "market_condition") {
    const variantMeaning = shortVariantMeaning(
      variantText,
      REGIME_VARIANT_MEANINGS[variantNorm] ?? `${variantLabel} conditions`,
    );
    return ensureSentence(`${intro}. ${variantLabel}: ${variantMeaning}.`);
  }

  if (variantText) {
    return ensureSentence(`${intro}. ${variantLabel}: ${shortVariantMeaning(variantText, variantLabel)}.`);
  }

  if (metricText) {
    return ensureSentence(`${intro}. Value: ${variantLabel}.`);
  }

  return ensureSentence(`${metricLabel}: ${variantLabel}.`);
}

function isCandleDirectionVariant(variantKey: string): boolean {
  const v = normalizeToken(variantKey);
  return v === "up" || v === "down" || v === "flat";
}

export function candleDirectionMetricKeyFromSliceKind(kind: string): "candle_direction" | "candle_direction_short" | null {
  const k = normalizeToken(kind);
  if (!k) return null;
  if (k.includes("short") && (k.includes("candle") || k.includes("direction"))) {
    return "candle_direction_short";
  }
  if (
    k === "market_direction" ||
    k === "candle_direction" ||
    k === "regime_direction" ||
    k === "regime_direction_trade_direction" ||
    k === "session_candle_direction" ||
    k === "session_regime_direction_trade_direction" ||
    (k.includes("candle") && k.includes("direction"))
  ) {
    return "candle_direction";
  }
  return null;
}

export function candleDirectionMetricKeyFromLabelText(
  labelText: string,
  sliceKind?: string,
  candleTimeframe?: string | null,
): "candle_direction" | "candle_direction_short" | null {
  const text = String(labelText ?? "");
  const horizon = directionHorizonFromBarsLabel(text, candleTimeframe);
  if (horizon === "short") return "candle_direction_short";
  if (horizon === "long") return "candle_direction";
  return sliceKind ? candleDirectionMetricKeyFromSliceKind(sliceKind) : null;
}

export type MarketContextVariantTooltipOptions = {
  sliceKind?: string;
  labelText?: string;
  candleTimeframe?: string | null;
};

function resolveCandleDirectionMetricKey(
  metricKey: string,
  options?: MarketContextVariantTooltipOptions,
): "candle_direction" | "candle_direction_short" | null {
  const m = normalizeToken(metricKey);
  if (m === "candle_direction_short" || m === "market_direction_short" || m === "market_candle_direction_short") {
    return "candle_direction_short";
  }
  if (m === "candle_direction" || m === "market_direction") return "candle_direction";
  if (m === "market_candle_direction" || (m.includes("market") && m.includes("candle") && m.includes("direction"))) {
    const fromLabel = options?.labelText
      ? candleDirectionMetricKeyFromLabelText(options.labelText, options.sliceKind, options.candleTimeframe)
      : null;
    if (fromLabel) return fromLabel;
    if (options?.sliceKind) return candleDirectionMetricKeyFromSliceKind(options.sliceKind);
    return "candle_direction";
  }
  if (options?.labelText) {
    const fromLabel = candleDirectionMetricKeyFromLabelText(
      options.labelText,
      options.sliceKind,
      options.candleTimeframe,
    );
    if (fromLabel) return fromLabel;
  }
  if (options?.sliceKind) return candleDirectionMetricKeyFromSliceKind(options.sliceKind);
  return null;
}

/**
 * Tooltip copy for a metric variant token, e.g. `consistency:high` or `ma50=below`.
 */
export function marketContextVariantTooltipText(
  metricKey: string,
  variantKey: string,
  metricDefinitions?: Record<string, unknown>,
  options?: MarketContextVariantTooltipOptions,
): string {
  const metric = String(metricKey ?? "").trim();
  const variant = String(variantKey ?? "").trim();
  if (!metric || !variant) return "";

  const defs = metricDefinitions ?? {};
  const metricDef = resolveMetricDefinition(metric, defs);
  const combinedKey = `${metric}:${variant}`;
  const directCombinedDef = defs[combinedKey];
  const directCombinedText =
    typeof directCombinedDef === "string" && directCombinedDef.trim() ? directCombinedDef.trim() : null;

  const metricText = metricDefinitionText(metricDef);
  const variantText = variantDefinitionText(metricDef, variant);

  if (directCombinedText) return ensureSentence(firstSentence(directCombinedText, 160));

  return composeBucketDefinitionTooltip(metric, variant, metricText, variantText, options);
}

function briefSituationLabel(metricKey: string, variantKey: string): string {
  const metricNorm = normalizeToken(metricKey);
  const variantLabel = humanizeToken(variantKey);
  if (metricNorm === "ma50" || metricNorm === "ma200") {
    const ma = metricNorm.toUpperCase();
    return `${variantLabel} ${ma}`;
  }
  if (metricNorm === "regime" || metricNorm === "market_condition") {
    return `${variantLabel} regime`;
  }
  if (metricNorm === "trade_direction" || metricNorm === "direction") {
    return `${variantLabel} trades`;
  }
  return `${variantLabel} ${humanizeToken(metricKey)}`;
}

function joinBriefPhrasesAsSentence(parts: string[], context?: "best" | "worst"): string {
  if (parts.length === 0) return "";
  const list =
    parts.length === 1
      ? parts[0]!
      : parts.length === 2
        ? `${parts[0]} and ${parts[1]}`
        : `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;

  if (context === "best") return ensureSentence(`Strongest situation here: ${list}.`);
  if (context === "worst") return ensureSentence(`Weakest situation here: ${list}.`);
  return ensureSentence(`Notable situation: ${list}.`);
}

function variantMeaningClause(
  metricKey: string,
  variantKey: string,
): string {
  return briefSituationLabel(metricKey, variantKey);
}

/** Short meaning-focused clause for a metric variant — used in combined situation tooltips. */
export function marketContextVariantTooltipBrief(
  metricKey: string,
  variantKey: string,
  _metricDefinitions?: Record<string, unknown>,
  _options?: MarketContextVariantTooltipOptions,
): string {
  return variantMeaningClause(metricKey, variantKey);
}

/** Compact tooltip for multi-clause situation labels (best/worst situation cells). */
export function situationBriefTooltip(
  text: string,
  metricDefinitions?: Record<string, unknown>,
  context?: "best" | "worst",
): string {
  const raw = String(text ?? "").trim();
  if (!raw || raw === "—") return "";
  const tooltipOptions = { labelText: raw };
  const clauses = raw.split("|").map((p) => p.trim()).filter(Boolean);
  const parts = clauses
    .map((clause) => {
      const [kRaw, ...vRest] = clause.split("=");
      const k = String(kRaw ?? "").trim();
      const v = vRest.join("=").trim();
      if (!k || !v) return "";
      return marketContextVariantTooltipBrief(k, v, metricDefinitions, tooltipOptions);
    })
    .filter(Boolean);
  return joinBriefPhrasesAsSentence(parts, context);
}

